import type { JSX } from "preact";
import { useEffect, useState } from "preact/hooks";
import { Auth } from "@gss/common/Auth";
import { resultsPaths as paths } from "@gss/common/Paths";
import { Roles } from "@gss/common/Roles";
import { useMessages } from "@gss/components/Messages";
import { NavMenu } from "@gss/components/NavMenu";
import { useParams } from "@gss/hooks/useParams";
import { DAO } from "../DAO";
import { Back } from "../util/Back";
import type { AugmentedExportResultForm } from "./export.types";
import { ExportAgg as ExportAggregated } from "./ExportAggregated";
import { ExportRaw } from "./ExportRaw";

type Tab = "aggregate" | "raw";

export function Export(): JSX.Element | null {
    const [currentTab, setCurrentTab] = useState<Tab>("aggregate");
    const [conf, setConf] = useState<AugmentedExportResultForm>();
    const { addError } = useMessages();
    const params = useParams();

    useEffect(() => {
        const measurementName = params.get("name");
        const periods = params.get("period");
        DAO.export
            .getResultForm(measurementName, periods)
            .then((result) => {
                setConf({
                    ...result,
                    measurementName,
                    periods,
                });
            })
            .catch(addError);
    }, []);

    const getNavbar = () => {
        if (!Auth.hasRole(Roles.EXPORT_RAW)) {
            return null;
        }
        const nodes = [
            {
                children: "Aggregera",
                value: "aggregate",
                onClick: () => setCurrentTab("aggregate"),
            },
            {
                children: "Rådata",
                value: "raw",
                onClick: () => setCurrentTab("raw"),
            },
        ];
        const activeNode = nodes.find((n) => n.value === currentTab);
        return (
            <div className="mb-3">
                <NavMenu variant="tabs" activeNode={activeNode}>
                    {nodes}
                </NavMenu>
            </div>
        );
    };

    const getTab = (conf: AugmentedExportResultForm) => {
        switch (currentTab) {
            case "aggregate":
                return <ExportAggregated conf={conf} />;
            case "raw":
                return <ExportRaw conf={conf} />;
            default: {
                const _ensureExhaustive: never = currentTab;
                throw new Error("Unknown tab value");
            }
        }
    };

    if (conf == null) {
        return null;
    }

    return (
        <div>
            <Back href={paths.exports} />
            <h1>
                {"Exportera resultat för "}
                {`${conf.measurementName} ${conf.periods}`}
            </h1>
            {getNavbar()}
            {getTab(conf)}
        </div>
    );
}
